// src/shared/hooks/useTimedLoading.js
import { useState, useEffect } from 'react';

/**
 * Tracks elapsed time for a loading state.
 * @param {number} warmupThresholdMs – milliseconds after which the warm‑up message appears (default 3000 ms).
 * @returns {{ isWarmup: boolean, start: () => void, reset: () => void }}
 *
 * Usage:
 *   const { isWarmup, start, reset } = useTimedLoading();
 *   start();   // call when request begins
 *   reset();   // call when request finishes
 */
export const useTimedLoading = (warmupThresholdMs = 3000) => {
  const [isWarmup, setIsWarmup] = useState(false);
  const [timerId, setTimerId] = useState(null);

  const start = () => {
    // Clear any existing timer
    if (timerId) clearTimeout(timerId);
    // After the threshold, set the warm‑up flag
    const id = setTimeout(() => setIsWarmup(true), warmupThresholdMs);
    setTimerId(id);
  };

  const reset = () => {
    if (timerId) clearTimeout(timerId);
    setIsWarmup(false);
    setTimerId(null);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerId) clearTimeout(timerId);
    };
  }, [timerId]);

  return { isWarmup, start, reset };
};